import { useDisclosure } from "@chakra-ui/react";
import {
  type BodyColumnDef,
  DataTable,
  TruncatedTextWithTooltip,
} from "@components/ui-kit";
import { t } from "@lingui/macro";
import {
  ContractOrderByColumnEnum,
  useGetContractsQuery,
  useUpdateContractMutation,
} from "@src/__generated__/graphql";
import { GoogleDriveLink } from "@src/components/ui-kit/GoogleDriveLink";
import { SharepointLink } from "@src/components/ui-kit/SharepointLink";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { dateTime } from "@src/utils/formatters";
import { FormMode } from "@src/utils/types";
import { observer } from "mobx-react-lite";
import React, {
  type FunctionComponent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ModalEditContractForm } from "../../form/contract/ModalEditContractForm";
import { StoreContractForm } from "../../form/contract/StoreContractForm";
import type { ContractModel } from "../../models";
import { DocumentsCtx, type StoreDocuments } from "../../StoreDocuments";
import { CellDocuments } from "../CellDocuments";
import { CellFiles } from "../CellFiles";
import { PopoverRowActions } from "./PopoverRowActions";

/**
 * encapsulate initial data fetch & refetch
 */
function useData(store: StoreDocuments) {
  const { loading, error, refetch } = useGetContractsQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      ...store.pagination.asParams,
      filters: {
        orderBy: store.orderByMixed.contracts.asWhereParam,
        where: store.whereMixed.contracts.asGraphQueryParam,
        search: store.searchTerm || undefined,
      },
    },
    onCompleted(data) {
      store.fromGetContractsQuery(data);
    },
    ...onError(),
  });

  store.refetchContracts = refetch;
  store.error = error;
  store.loading = loading;
}

type TData = {
  id: ContractModel["id"];
  title: ContractModel["title"];
  type: ContractModel["type"];
  validFrom: ContractModel["validFrom"];
  relatedTo?: string[];
  validTo: ContractModel["validTo"];
  files: ContractModel["files"];
  documents: string;
  actions: string;
  cloudUrl: ContractModel["cloudUrl"];
  driveCloudUrl: ContractModel["driveCloudUrl"];
  _model: ContractModel;
};

export const TableContracts = observer(function TableContracts() {
  const store = useContext(DocumentsCtx);

  useData(store);

  const data: TData[] = store.contracts.map((r) => ({
    id: r.id,
    title: r.title,
    type: r.type,
    relatedTo: [
      ...r.projects.map(({ title }) => title),
      ...r.clients.map(({ name }) => name),
      ...r.contractors.map(({ legalName }) => legalName),
      ...r.brands.map(({ name }) => name),
    ],
    validFrom: r.validFrom,
    validTo: r.validTo,
    files: r.files,
    documents: "",
    actions: "",
    cloudUrl: r.cloudUrl,
    driveCloudUrl: r.driveCloudUrl,
    _model: r,
  }));

  const columns = useMemo<BodyColumnDef<TData, ContractOrderByColumnEnum>[]>(
    () => [
      {
        id: "title",
        meta: { label: t`Title` },
        header: () => t`Title`,
        orderBy: ContractOrderByColumnEnum.Title,
        cell: ({ row }) => <TitleCell r={row.original} />,
      },
      {
        id: "related-to",
        meta: { label: t`Related to` },
        header: () => t`Related to`,
        cell: ({ row }) => {
          {
            const relatedToText = row.original.relatedTo?.join(", ");
            return (
              <TruncatedTextWithTooltip text={relatedToText} maxWidth="270px" />
            );
          }
        },
      },
      {
        id: "type",
        meta: { label: t`Type` },
        header: () => t`Type`,
      },
      {
        id: "valid-from",
        meta: { label: t`Valid from` },
        header: () => t`Valid from`,
        orderBy: ContractOrderByColumnEnum.ValidFrom,
        cell: ({ row }) => dateTime.format(row.original.validFrom),
      },
      {
        id: "valid-to",
        meta: { label: t`Valid to` },
        header: () => t`Valid to`,
        orderBy: ContractOrderByColumnEnum.ValidTo,
        cell: ({ row }) =>
          row.original.validTo
            ? dateTime.format(row.original.validTo)
            : t`Indefinite`,
      },
      {
        id: "files",
        meta: { label: t`Files` },
        header: () => t`Files`,
        cell: ({ row }) => <CellFiles files={row.original.files} />,
      },
      {
        id: "documents",
        meta: { label: t`Documents` },
        header: () => t`Documents`,
        cell: ({ row }) => (
          <CellDocuments
            data={{
              download: row.original._model.urls?.download,
              previews: row.original._model.files,
            }}
          />
        ),
      },
      {
        id: "sharepoint",
        meta: { label: t`Sharepoint` },
        header: () => t`Sharepoint`,
        cell: ({ row }) =>
          row.original.cloudUrl && (
            <SharepointLink href={row.original.cloudUrl} />
          ),
      },
      {
        id: "drive",
        meta: { label: t`Google Drive` },
        header: () => t`Google Drive`,
        cell: ({ row }) =>
          row.original.driveCloudUrl && (
            <GoogleDriveLink href={row.original.driveCloudUrl} />
          ),
      },
      {
        id: "actions",
        meta: { label: t`Actions` },
        header: () => t`Actions`,
        cell: ({ row }) =>
          can("contract_delete", "contract_update") && (
            <PopoverRowActions contract={row.original._model} />
          ),
      },
    ],
    [],
  );

  const visibleColumns = useMemo(() => {
    if (store.asExternalModule) {
      return columns.filter(
        (column) =>
          !column.id ||
          !store.hideColsInExternalModule.otherDocuments.includes(column.id),
      );
    }
    return columns;
  }, [
    columns,
    store.asExternalModule,
    store.hideColsInExternalModule.otherDocuments,
  ]);

  return (
    <DataTable.Body
      tableKey="contracts"
      data={data}
      columns={visibleColumns}
      orderedBy={store.orderByMixed.contracts.clauses[0]}
      onOrderByChange={store.handleContractsOrderByChange}
    />
  );
});

const TitleCell: FunctionComponent<React.PropsWithChildren<{ r: TData }>> =
  observer(function TitleCell({ r }) {
    const pageStore = useContext(DocumentsCtx);

    const [store] = useState(() => {
      const store = StoreContractForm.create();
      return store;
    });

    const editModal = useDisclosure();

    const [update, updateReq] = useUpdateContractMutation({
      onCompleted() {
        pageStore.refetchContracts?.();
        editModal.onClose();
      },
      ...onError(),
    });

    useEffect(() => {
      if (editModal.isOpen) store.fillContractForm(r._model);
    }, [editModal.isOpen]);

    return (
      <React.Fragment>
        {can("contract_update") ? (
          <button
            {...editModal.getButtonProps()}
            className="Polaris-Link outline-none border-none"
          >
            {r.title}
          </button>
        ) : (
          <span>{r.title}</span>
        )}
        <ModalEditContractForm
          {...editModal.getDisclosureProps()}
          {...editModal}
          store={store}
          mode={FormMode.EDIT}
          title={t`Edit contract`}
          primaryActionContent={t`Save changes`}
          defaultContractorName={r._model.contractors[0]?.legalName}
          loading={updateReq.loading}
          onSubmit={(vars) => {
            update({ variables: { input: { ...vars, id: r._model.id } } });
          }}
        />
      </React.Fragment>
    );
  });
